import type { PlanDay, PlanInput, PlanType } from '@painpal/contracts';
import { estimatePlan, PLAN_NOTE_MAX, PLAN_TYPES } from '@painpal/core';
import { Check, Trash2, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type FormEvent, type RefObject } from 'react';
import { useClearPlanDay, useSavePlanDay } from '../../api/mutations';
import { errorMessage } from '../../lib/api';
import { formatHoursMinutes, formatWeekdayDayMonth } from '../../lib/format';
import { PLAN_COLORS } from '../../theme/tokens';
import { BottomSheet } from '../../ui/BottomSheet';
import { usePresent } from '../../ui/presentContext';
import { useIsPhone } from '../../ui/useMediaQuery';
import { useModalFocus } from '../../ui/useModalFocus';
import { defaultDuration, editorEyebrow, estimateMath, formatEstimate, maskDuration, parseDuration, PLAN_LABELS, type DurationInput } from './planModel';
import styles from './dayEditor.module.css';

export interface DayEditorProps { day: PlanDay; onClose: () => void }

// Outside means outside the row item the editor is anchored in, so a click on its own row keeps it open.
// The scrim is skipped because its own click closes the sheet. An exiting editor stops listening, so it can't close the next one.
function useOutsidePointer(ref: RefObject<HTMLElement | null>, onOutside: () => void): void {
  const present = usePresent();
  useEffect(() => {
    if (!present) return undefined;
    const onPointerDown = (event: Event): void => {
      const area = ref.current?.closest('[data-editor-anchor]') ?? ref.current;
      const target = event.target;
      if (!(target instanceof Element) || area?.contains(target) === true || target.closest('[data-part="scrim"]') !== null) return;
      onOutside();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [ref, onOutside, present]);
}

const REST_DURATION: DurationInput = { ok: true, seconds: null };

function useBlockDraft(day: PlanDay) {
  const [type, setType] = useState<PlanType | null>(day.plan?.type ?? null);
  const [duration, setDuration] = useState(day.plan?.durationS == null ? '' : formatHoursMinutes(day.plan.durationS));
  const [note, setNote] = useState(day.plan?.note ?? '');
  const parsed = type === 'rest' ? REST_DURATION : parseDuration(duration);
  const seconds = parsed.ok ? parsed.seconds : null;
  const trimmed = note.trim();
  const input: PlanInput | null = type === null ? null : { type, durationS: seconds, note: trimmed === '' ? null : trimmed };
  return { type, setType, duration, setDuration, note, setNote, parsed, seconds, input };
}

function TypeChips({ name, value, onChange }: { name: string; value: PlanType | null; onChange: (type: PlanType) => void }) {
  return (
    <div className={styles.field}>
      <span className={styles.label} aria-hidden="true">TYPE</span>
      <div className={styles.chips} role="radiogroup" aria-label="Type">
        {PLAN_TYPES.map((type) => {
          const checked = value === type;
          return (
            <label key={type} className={styles.chip} data-checked={checked || undefined} style={checked ? { background: PLAN_COLORS[type] } : undefined}>
              <input type="radio" className={styles.radio} name={name} value={type} checked={checked} onChange={() => onChange(type)} />
              {checked
                ? <Check size={12} aria-hidden="true" />
                : <span className={styles.swatch} data-type={type} style={{ background: PLAN_COLORS[type] }} aria-hidden="true" />}
              {PLAN_LABELS[type]}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function DurationField({ type, value, onChange }: { type: PlanType | null; value: string; onChange: (value: string) => void }) {
  const id = useId();
  const rest = type === 'rest';
  return (
    <div className={`${styles.field} ${styles.duration}`}>
      <label className={styles.label} htmlFor={id}>DURATION · OPTIONAL</label>
      <div className={styles.input}>
        <input
          id={id} className={styles.durationInput} inputMode="numeric" autoComplete="off" disabled={rest} value={rest ? '' : value}
          placeholder={type === null || rest ? '' : defaultDuration(type)} onChange={(e) => onChange(maskDuration(e.currentTarget.value))}
        />
        <span className={styles.suffix} aria-hidden="true">H:MM</span>
      </div>
    </div>
  );
}

function Estimate({ type, seconds }: { type: PlanType | null; seconds: number | null }) {
  return (
    <div className={styles.estimate}>
      <span className={styles.quiet}>ESTIMATED · LIVE</span>
      <p className={styles.estimateValue} aria-live="polite" aria-atomic="true">
        <span className={styles.estimateFigure}>{type === null ? '—' : formatEstimate(estimatePlan(type, seconds).load)}</span>
        <span className={styles.unit}>LOAD</span>
      </p>
      <span className={styles.quiet}>{type === null ? 'PICK A TYPE' : estimateMath(type, seconds)}</span>
    </div>
  );
}

function NoteField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const id = useId();
  return (
    <div className={styles.field}>
      <div className={styles.noteHead}>
        <label className={styles.label} htmlFor={id}>NOTE · OPTIONAL</label>
        <span className={styles.quiet}>{`${value.length} / ${PLAN_NOTE_MAX}`}</span>
      </div>
      <div className={styles.input}>
        <input id={id} className={styles.noteInput} maxLength={PLAN_NOTE_MAX} autoComplete="off" value={value} onChange={(e) => onChange(e.currentTarget.value)} />
      </div>
    </div>
  );
}

interface ActionsProps { isPhone: boolean; canClear: boolean; clearing: boolean; onClear: () => void; canSave: boolean; onCancel: () => void }

// Each layout gets its own DOM order so tab and reading order follow what's on screen. The keys stop React reusing
// one action's button for another across a flip.
function Actions({ isPhone, canClear, clearing, onClear, canSave, onCancel }: ActionsProps) {
  const clear = canClear && (
    <button key="clear" type="button" className={`${styles.textButton} ${styles.clear}`} data-action="clear" disabled={clearing} onClick={onClear}>
      <Trash2 size={14} aria-hidden="true" />CLEAR DAY
    </button>
  );
  const cancel = <button key="cancel" type="button" className={`${styles.textButton} ${styles.cancel}`} data-action="cancel" onClick={onCancel}>CANCEL</button>;
  const save = <button key="save" type="submit" className={styles.save} data-action="save" disabled={!canSave}>SAVE</button>;
  if (isPhone) return <div className={styles.actions}>{save}{clear}{cancel}</div>;
  return <div className={styles.actions}>{clear}<div key="right" className={styles.actionsRight}>{cancel}{save}</div></div>;
}

// The layouts order the actions differently, so React remounts them on a flip; put focus back on the same action.
function useActionFocusAcrossLayouts(isPhone: boolean) {
  const form = useRef<HTMLFormElement>(null);
  const focused = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (focused.current === null || form.current?.contains(document.activeElement) === true) return;
    form.current?.querySelector<HTMLElement>(`[data-action="${focused.current}"]`)?.focus();
  }, [isPhone]);
  const onFocus = (event: FocusEvent<HTMLFormElement>): void => {
    focused.current = event.target.getAttribute('data-action');
  };
  return { form, onFocus };
}

function EditorForm({ day, onClose }: DayEditorProps) {
  const draft = useBlockDraft(day);
  const save = useSavePlanDay();
  const clear = useClearPlanDay();
  const isPhone = useIsPhone();
  const focus = useActionFocusAcrossLayouts(isPhone);
  // The alert holds what SAVE was refused for, so it changes on submit rather than on every keystroke.
  const [submitted, setSubmitted] = useState<DurationInput | null>(null);
  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSubmitted(draft.parsed);
    if (!draft.parsed.ok || draft.input === null) return;
    save.mutate({ date: day.date, input: draft.input }, { onSuccess: onClose });
  }
  const failed = save.error ?? clear.error;
  const invalid = submitted !== null && !submitted.ok && !draft.parsed.ok ? submitted.error : null;
  const message = invalid ?? (failed === null ? null : errorMessage(failed));
  return (
    <form ref={focus.form} className={styles.form} onSubmit={submit} onFocus={focus.onFocus} aria-label="Day block" noValidate>
      <TypeChips name={`plan-type-${day.date}`} value={draft.type} onChange={draft.setType} />
      <div className={styles.row}>
        <DurationField type={draft.type} value={draft.duration} onChange={draft.setDuration} />
        <Estimate type={draft.type} seconds={draft.seconds} />
      </div>
      <NoteField value={draft.note} onChange={draft.setNote} />
      {message !== null && <p role="alert" className="alert-hot">{message}</p>}
      <Actions
        isPhone={isPhone} canClear={day.plan !== null} clearing={clear.isPending} onClear={() => clear.mutate(day.date, { onSuccess: onClose })}
        canSave={draft.type !== null && !save.isPending} onCancel={onClose}
      />
    </form>
  );
}

export function DayEditor({ day, onClose }: DayEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalFocus(ref, onClose);
  useOutsidePointer(ref, onClose);
  return (
    <BottomSheet onClose={onClose}>
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId} className={styles.editor} data-motion="popover">
        <svg className={styles.caret} width="14" height="20" viewBox="0 0 14 20" aria-hidden="true">
          <path d="M14 0l-14 10 14 10" fill="var(--paper)" stroke="var(--ink)" strokeWidth="2" />
        </svg>
        <div className={styles.head}>
          <div className={styles.headText}>
            <p className={styles.eyebrow}>{editorEyebrow(day)}</p>
            <h2 id={titleId} className={styles.title}>{formatWeekdayDayMonth(day.date)}</h2>
          </div>
          <button type="button" className={styles.close} aria-label="Close editor" onClick={onClose}>
            <span className={styles.closeBox}><X size={14} aria-hidden="true" /></span>
          </button>
        </div>
        <EditorForm day={day} onClose={onClose} />
      </div>
    </BottomSheet>
  );
}
